/**
 * ResearchResultPage — dedicated page for viewing a single research job.
 *
 * Route: /research/:jobId
 * Shows progress if still running, results when complete.
 * Shareable URL: anyone IN THE OWNER'S ORGANIZATION with the link can view
 * results (cross-tenant blocked; cancel/delete stay owner-only). Enforced
 * server-side in research_routes._enforce_job_access.
 */
import { useState, useEffect, useCallback, useContext } from 'react';
import DOMPurify from 'dompurify';
import { useParams, Link, useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import { useResearchStream } from '../hooks/useResearchStream';
import { formatVersionDate, labelPrefixForRegion } from '../utils/labelVersionFormat';
import { LetterResultRow } from '../components/LetterResultRow';
import ApprovalLetterResearchResults, {
  type ApprovalLetterResearchResult,
} from '../components/ApprovalLetterResearchResults';
import { StopReasonBanner } from '../components/StopReasonBanner';
import type { LetterKeywordResultItem, CIResearchResult, Corpus } from '../types';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';
import { resolveDraftTarget } from '../utils/resolveDraftTarget';
import { safeCompetitiveIntelUrl } from '../utils/competitiveIntelUrl';
import type { DraftHitItem } from '../types';
import { useReportResearchRun } from '../shell/useReportResearchRun';
import { ShellContext } from '../shell/shellContext';
import { useTab } from '../shell/TabContext';
import { withDetail } from '../shell/tabState';

/**
 * Render a deep-search timeline entry header so it matches the version
 * dropdown style elsewhere (e.g. "USPI - V7 → V8 - Jan 15 2024").  Research
 * is FDA-only for now, so the region defaults to ``US`` (→ "USPI"); when
 * EU research lands, thread the result's region through here.  Falls back
 * gracefully when ``date`` or ``version_pair`` is missing.
 */
function formatTimelineHeader(
  versionPair: string,
  date: string,
  region: string | null | undefined = 'US',
): string {
  // Normalise the LLM output: "v5 → v6" → "V5 → V6" (matches the new
  // label-version format).  Doesn't touch tokens that aren't ``vN`` so
  // pre-formatted strings pass through unchanged.
  const normalisedPair = (versionPair || '').replace(/\bv(\d+)\b/g, 'V$1');
  const dateStr = formatVersionDate(date);
  const prefix = labelPrefixForRegion(region);
  return [prefix, normalisedPair, dateStr].filter(Boolean).join(' - ');
}

const API_URL = import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ResearchJob {
  id: string;
  query: string;
  max_results: number;
  status: string;
  progress_pct: number;
  progress_message: string | null;
  drugs_found: number;
  drugs_analyzed: number;
  drugs_total: number;
  // Letter-corpus fields (Task 15).  Optional / null for drug jobs.  The
  // backend mirrors ``drugs_*`` into ``letters_*`` only when
  // ``corpus == 'fda_letters'``; we read ``job.corpus`` to decide which
  // counter pair to display in the header.
  //
  // ``'ci'`` (Plan-4 Task 5, competitive-intel): the backend does NOT
  // mirror a dedicated ``ci_*`` counter pair — a ci job reuses the shared
  // ``drugs_found``/``drugs_analyzed``/``drugs_total`` columns directly
  // (see ``ci_research_service.py``), so the ci branches below read those
  // fields with a "document(s)" noun instead of drug-shaped copy.
  corpus?: Corpus;
  doc_types?: string[] | null;
  letters_found?: number | null;
  letters_analyzed?: number | null;
  letters_total?: number | null;
  summary: string | null;
  error_message: string | null;
  parent_job_id: string | null;
  strategy_type: string | null;
  // Phase 2 of the unified research pipeline plan — both optional
  // because legacy job rows that pre-date the metadata stamp at
  // research_service.py surface as null.
  stop_reason?: string | null;
  total_evaluated?: number | null;
  notice?: string | null;
  synthesis: {
    patterns?: Array<{ name: string; description: string; drugs: string[] }>;
    class_timeline?: Array<{ date: string; drug: string; event: string; significance: string }>;
  } | null;
  created_at: string;
  completed_at: string | null;
}

interface ResearchResult {
  drug_id: number;
  drug_name: string;
  application_number: string | null;
  match: boolean;
  score: number;
  rank: number | null;
  analysis: string | null;
  evidence: {
    tool_results?: Record<string, any>;
    chunks?: any[];
    sba_chunks?: ResearchReviewChunk[];
  } | null;
}

interface ResearchSubmissionAssociation {
  fda_submission_id: number;
  submission_type?: string | null;
  submission_number?: number | null;
  submission_status_date?: string | null;
}

interface ResearchReviewChunk {
  document_id: number;
  chunk_id: number;
  document_type?: string;
  doc_type?: string | null;
  logical_document_key?: string;
  source_document_name?: string | null;
  effective_doc_type_labels?: string[];
  submission_associations?: ResearchSubmissionAssociation[];
  [k: string]: unknown;
}

export interface ResearchReviewSource {
  key: string;
  representative: ResearchReviewChunk;
  chunks: ResearchReviewChunk[];
}

/**
 * Collapse physical split rows/chunks to one Investigator source citation.
 * Different submissions remain separate because their logical source keys do.
 */
export function multiReviewSources(r: ResearchResult): ResearchReviewSource[] {
  const sources = new Map<string, ResearchReviewSource>();
  for (const chunk of r.evidence?.sba_chunks ?? []) {
    const key = chunk.logical_document_key || `physical:${chunk.document_id}`;
    const existing = sources.get(key);
    if (existing) {
      existing.chunks.push(chunk);
    } else {
      sources.set(key, { key, representative: chunk, chunks: [chunk] });
    }
  }
  return [...sources.values()];
}

function reviewSubmissionLabel(
  association: ResearchSubmissionAssociation,
  compact: boolean = false,
): string {
  const type = association.submission_type?.toUpperCase();
  if (type === 'ORIG') return compact ? 'Original' : 'Original submission';
  if (type === 'SUPPL') {
    if (association.submission_number == null) return 'Supplement';
    return compact
      ? `Suppl ${association.submission_number}`
      : `Supplement ${association.submission_number}`;
  }
  if (association.submission_type) {
    return association.submission_number == null
      ? association.submission_type
      : `${association.submission_type} ${association.submission_number}`;
  }
  return 'Unclassified';
}

export function reviewSourceLabel(
  source: ResearchReviewSource,
  compact: boolean = false,
): string {
  const chunk = source.representative;
  if (
    chunk.submission_associations === undefined
    && chunk.logical_document_key === undefined
  ) {
    const family = chunk.document_type === 'epar' ? 'EPAR' : 'SBA';
    return compact
      ? `${family} #${chunk.document_id}`
      : `${family} document #${chunk.document_id}`;
  }
  const typeLabels = chunk.effective_doc_type_labels?.length
    ? chunk.effective_doc_type_labels
    : [chunk.doc_type?.replace(/_/g, ' ') || 'FDA review'];
  const associations = chunk.submission_associations;
  const versionLabels = associations?.length
    ? associations.map(item => reviewSubmissionLabel(item, compact))
    : ['Unclassified'];
  return compact
    ? versionLabels.join(' + ')
    : `${typeLabels.join(' + ')} · ${versionLabels.join(' + ')}`;
}

/**
 * Letter-shaped research result row.  Mirrors
 * ``LetterResearchResultResponse`` from
 * ``backend/app/api/research_routes.py`` (Phase E).  ``best_snippet`` is
 * optional / nullable because the column is freshly populated by the
 * evaluator; older rows / failure cases can leave it ``null``.
 */
interface LetterResearchResult {
  letter_id: number;
  letter_type: 'warning' | 'untitled' | 'promotional';
  letter_title?: string | null;
  issued_date?: string | null;
  recipient?: string | null;
  drug_id?: number | null;
  drug_name?: string | null;
  match: boolean;
  score: number;
  rank: number | null;
  best_snippet?: string | null;
  // Snapshot of role-tagged drug references at evaluation time.
  // Mirrors ``LetterResearchResultResponse.matched_references`` on the
  // backend.  Defaults to ``[]`` for older API responses (pre-Task-6
  // rows persist NULL → JSON null on the wire).
  matched_references?: {
    drug_id: number;
    drug_name: string;
    role: 'target' | 'comparator' | 'mentioned';
  }[];
}

/**
 * Draft-document-shaped research result row.  Mirrors
 * ``DraftResearchResultResponse`` from
 * ``backend/app/api/research_routes.py`` — returned inside
 * ``draft_results`` when ``job.corpus === 'label_negotiations'``.  Keyed
 * on the draft document (``label_version_id``).  ``snippets`` reuses the
 * shared ``DraftHitItem`` shape (the backend persists each hit as
 * ``{ kind, section_ref, snippet, comment_id }``).
 */
interface DraftResearchResultResponse {
  label_version_id: number;
  drug_id: number | null;
  document_name: string | null;
  relevant: boolean;
  answer: string | null;
  score: number;
  snippets: DraftHitItem[] | null;
}

interface JobDetail {
  job: ResearchJob;
  // Drug-shaped results — populated only when ``job.corpus === 'labels'``.
  results: ResearchResult[];
  // Letter-shaped results — populated only when
  // ``job.corpus === 'fda_letters'``.  Defaults to ``[]`` defensively so
  // older API responses (pre-Phase E) don't crash render.
  letter_results: LetterResearchResult[];
  // Draft-document-shaped results — populated only when
  // ``job.corpus === 'label_negotiations'``.  Optional / defaults to ``[]``
  // defensively so non-draft jobs (and older API responses) don't crash render.
  draft_results?: DraftResearchResultResponse[];
  // Page/press-release-shaped results — populated only when
  // ``job.corpus === 'ci'``. Mutually exclusive with the other three lists;
  // optional / defaults to ``[]`` defensively for older API responses.
  ci_results?: CIResearchResult[];
  approval_letter_results?: ApprovalLetterResearchResult[];
}

/**
 * Convert a ``LetterResearchResult`` into the
 * ``LetterKeywordResultItem`` shape ``LetterResultRow`` expects.
 *
 * ``LetterResultRow`` currently asks for a non-null ``best_snippet`` /
 * ``rank``; research letters may have ``null`` for either field, so we
 * coerce to safe defaults (empty string / -1) instead of widening the
 * row's prop type.  Behavioural impact:
 *   - Empty snippet → the snippet paragraph collapses (the row's own
 *     ``{letter.best_snippet && …}`` guard handles falsy values).
 *   - Rank ``-1``    → unused by ``LetterResultRow`` (only the keyword
 *     results table consumes ``rank`` for ordering).
 */
function letterResearchResultToRowItem(
  r: LetterResearchResult,
): LetterKeywordResultItem {
  return {
    letter_id: r.letter_id,
    letter_type: r.letter_type,
    letter_title: r.letter_title ?? null,
    issued_date: r.issued_date ?? null,
    recipient: r.recipient ?? null,
    drug_id: r.drug_id ?? null,
    drug_name: r.drug_name ?? null,
    best_snippet: r.best_snippet ?? '',
    score: r.score,
    rank: r.rank ?? -1,
    // ``?? []`` defends against pre-Task-6 rows where the JSONB column
    // can still serialize as ``null`` on the wire.
    matched_references: r.matched_references ?? [],
  };
}

// ---------------------------------------------------------------------------
// Exported pure helpers — used by ResultCard + tests
// ---------------------------------------------------------------------------

/**
 * Determine the review family for a multi_review result.
 *
 * Returns 'epar' when the first sba_chunk carries document_type === 'epar'
 * (EU/EMA Assessment Report), otherwise 'sba' (US FDA MDR/SBA, the default
 * for legacy rows that pre-date the document_type stamp).
 *
 * Exported so it can be unit-tested independently of the render tree.
 */
export function multiReviewFamily(r: ResearchResult): 'sba' | 'epar' {
  return ((r.evidence?.sba_chunks?.[0] as any)?.document_type as 'sba' | 'epar') ?? 'sba';
}

/**
 * Build the primary href for a drug research result.
 *
 * - multi_review corpus: links to the SBA viewer via the first sba_chunk;
 *   falls back to the label section form if no chunks are present.
 * - labels / fda_letters / undefined: uses the label section form.
 *
 * Note: the ``label_negotiations`` (draft) corpus does NOT flow through
 * ``ResearchResult``/``results`` — it has its own ``draft_results`` array
 * rendered by ``DraftResultRow`` (which builds its own draft hrefs via
 * ``resolveDraftTarget``).
 */
export function resultPrimaryHref(r: ResearchResult, corpus?: string): string {
  if (corpus === 'multi_review') {
    const c0 = r.evidence?.sba_chunks?.[0];
    const href = c0 && regulatoryCitationHref({
      document_type: (c0 as any).document_type ?? 'sba',
      drug_id: r.drug_id,
      document_id: c0.document_id,
      chunk_id: c0.chunk_id,
    } as any);
    if (href) return href;
  }
  // Fall-through: label section form (existing behaviour)
  const toolResults = r.evidence?.tool_results || {};
  const sectionsReferenced = new Set<string>();
  Object.values(toolResults).forEach((val: any) => {
    if (val?.args?.section_number) sectionsReferenced.add(val.args.section_number);
    if (val?.args?.sections) val.args.sections.forEach((s: string) => sectionsReferenced.add(s));
  });

  // ANCHORED (historical_snapshot) results were evaluated against a SPECIFIC
  // older version, not the live label — the findings quote "§14.1 (v1,
  // 2017-08-03)". Linking to the current label sends the reader to a document
  // that does not contain the quoted text, which reads as a fabricated
  // citation. ``anchored_sections`` already carries the resolved
  // ``drug_versions.id``, so deep-link to it via the ?source=historical&version=
  // route DrugDetailPage already honours.
  //
  // ``no_history_fallback`` means the drug had NO history and its current label
  // was treated as v1 — there is no historical row to open, so keep the
  // current-label link.
  const anchored: any[] = (r.evidence as any)?.anchored_sections || [];
  const anchor = anchored.find(
    (a) => a?.version_id != null && !a?.no_history_fallback,
  );
  // Prefer the sections the ANCHOR actually grounded on; the tool-result
  // sections above can name sections that only exist on the current label.
  if (anchor) {
    anchored.forEach((a) => {
      if (a?.section_number) sectionsReferenced.add(String(a.section_number));
    });
  }

  const sectionLinks = Array.from(sectionsReferenced).sort();
  const sectionsParam = `sections=${sectionLinks.join(',')}`;
  if (anchor) {
    // ``hsection`` (not ``section``): the ``section`` handler resets the viewer
    // to the CURRENT label before scrolling, which would undo the version we
    // are deep-linking to. ``hsection`` is handled by the historical-scroll
    // effect instead. Prefer the section the anchor actually grounded on.
    const target = anchor.section_number ?? sectionLinks[0];
    const hsection = target ? `&hsection=${encodeURIComponent(String(target))}` : '';
    return `/drug/${r.drug_id}?source=historical&version=${anchor.version_id}${hsection}&${sectionsParam}`;
  }
  return `/drug/${r.drug_id}?${sectionsParam}`;
}

/**
 * The anchored ``drug_versions.id`` a historical_snapshot result was evaluated
 * against, or null for an ordinary current-label result.
 *
 * ``no_history_fallback`` entries are excluded on purpose: they mean the drug
 * has no history and its CURRENT label was treated as v1, so there is no
 * historical row to open.
 */
export function resultAnchorVersionId(r: ResearchResult): number | null {
  const anchored: any[] = (r.evidence as any)?.anchored_sections || [];
  const hit = anchored.find(
    (a) => a?.version_id != null && !a?.no_history_fallback,
  );
  return hit ? Number(hit.version_id) : null;
}

/** Deep-link to ONE section, on the anchored version when there is one. */
export function resultSectionHref(r: ResearchResult, sectionNumber: string): string {
  const versionId = resultAnchorVersionId(r);
  // ``hsection`` on the anchored path — ``section`` would reset the viewer back
  // to the current label before scrolling. See resultPrimaryHref.
  return versionId != null
    ? `/drug/${r.drug_id}?source=historical&version=${versionId}`
      + `&hsection=${encodeURIComponent(sectionNumber)}`
    : `/drug/${r.drug_id}?section=${encodeURIComponent(sectionNumber)}`;
}

/**
 * Build the draft-viewer href for a draft research result row (or one of
 * its snippets).  Delegates path/query construction to the shared pure
 * ``resolveDraftTarget`` helper, then serialises ``query`` into a search
 * string.  A ``comment`` snippet (with a ``comment_id``) deep-links to the
 * matching comment; a ``content`` snippet / the row itself opens the draft
 * root.  Returns ``null`` when ``drug_id`` is missing (the draft viewer
 * route is keyed on ``drug_id``, so an unlinked draft has no target).
 */
function draftResultHref(
  r: DraftResearchResultResponse,
  hit?: DraftHitItem,
): string | null {
  if (r.drug_id == null) return null;
  const isComment = hit?.kind === 'comment' && hit.comment_id != null;
  const { path, query } = resolveDraftTarget(
    { drug_id: r.drug_id, label_version_id: r.label_version_id },
    isComment
      ? { kind: 'comment', comment_id: hit!.comment_id! }
      : { kind: 'content' },
  );
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(query).map(([k, v]) => [k, String(v)])),
  ).toString();
  return `${path}${qs ? `?${qs}` : ''}`;
}

// Section labels
const sectionLabels: Record<string, string> = {
  '1': 'Indications', '2': 'Dosage', '3': 'Dosage Forms',
  '4': 'Contraindications', '5': 'Warnings', '6': 'Adverse Reactions',
  '7': 'Drug Interactions', '8': 'Specific Populations',
  '10': 'Overdosage', '11': 'Description', '12': 'Clinical Pharmacology',
  '13': 'Nonclinical Toxicology', '14': 'Clinical Studies',
  '16': 'How Supplied', '17': 'Patient Counseling',
};

const toolLabels: Record<string, string> = {
  'get_historical_versions': 'Historical versions reviewed',
  'get_section_diff': 'Section diff compared',
  'read_full_section': 'Full section read',
  'search_other_drugs': 'Cross-drug search',
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function ResearchResultPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  // Registers the finished run with the shell (once per job). Must precede the early returns.
  const { running: stillRunning } = useReportResearchRun(jobId, detail ? detail.job.query : null);

  // Name this tab like Cogni's: "Investigator: <query>" (see GenericPage).
  const { tabId } = useTab();
  const shell = useContext(ShellContext);
  const tabTitle = shell?.tabs.find((t) => t.id === tabId)?.title;
  const tabName = detail ? withDetail('Investigator', detail.job.query) : null;
  useEffect(() => {
    if (shell && tabName && tabTitle !== undefined && tabTitle !== tabName) {
      shell.setTabTitle(tabId, tabName);
    }
  }, [shell, tabId, tabTitle, tabName]);

  // Stream for live progress if job is still running
  const isRunning = detail?.job.status && ['pending', 'running', 'analyzing'].includes(detail.job.status);
  const stream = useResearchStream(isRunning ? jobId || null : null);

  // Load job detail
  const loadDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/v2/research/${jobId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...getImpersonationHeaders(),
        },
      });
      if (!res.ok) {
        setError(`Failed to load: ${res.status}`);
        return;
      }
      const data = await res.json();
      setDetail(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Initial load
  useEffect(() => { loadDetail(); }, [loadDetail]);


  // Poll while running
  useEffect(() => {
    if (!isRunning) return undefined;
    const interval = setInterval(loadDetail, 10000);
    return () => clearInterval(interval);
  }, [isRunning, loadDetail]);

  // Reload when stream completes
  useEffect(() => {
    if (stream.status === 'complete' || stream.status === 'cancelled') {
      loadDetail();
    }
  }, [stream.status, loadDetail]);

  const handleShare = async () => {
    // In a shell tab the browser URL is the shell's; this tab's own location is the share link.
    const url = window.location.origin + location.pathname + location.search;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-slate-400">Loading Investigator results...</div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-red-600 bg-red-50 px-4 py-3 rounded-lg text-sm">
            {error || 'Research job not found'}
          </div>
          <Link to="/?mode=researcher" className="text-sm text-indigo-600 hover:text-indigo-800 mt-4 inline-block">
            ← Back to Deep Search
          </Link>
        </div>
      </div>
    );
  }

  const { job } = detail;
  const isLetterCorpus = job.corpus === 'fda_letters';
  const isMdrCorpus = job.corpus === 'multi_review';
  const isDraftCorpus = job.corpus === 'label_negotiations';
  const isCiCorpus = job.corpus === 'ci';
  const isApprovalLetterCorpus = job.corpus === 'approval_letters';
  // Job-level family label: derived from the first result's chunk document_type.
  // Falls back to 'sba' when results are empty (job still running) or legacy rows
  // pre-date the document_type stamp — matches the per-card multiReviewFamily logic.
  const mdrJobFamily: 'sba' | 'epar' = isMdrCorpus && detail.results[0] != null
    ? multiReviewFamily(detail.results[0])
    : 'sba';
  // Sort: by score descending, then alphabetically within same score.
  // For letter jobs we sort by score then letter_title; drug jobs sort
  // by score then drug_name (same pattern, different tie-breaker).
  const results = [...detail.results].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.drug_name.localeCompare(b.drug_name);
  });
  // Defensive default for older API responses that don't include
  // ``letter_results`` (pre-Phase E).  Sort is duplicated rather than
  // generalised because the tie-break field differs.
  const letterResults = [...(detail.letter_results ?? [])].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const an = a.letter_title || `Letter ${a.letter_id}`;
    const bn = b.letter_title || `Letter ${b.letter_id}`;
    return an.localeCompare(bn);
  });
  // Draft-document-shaped results (``label_negotiations`` corpus).  Same
  // defensive default + score-desc sort as the letter path; tie-break on the
  // document name (falling back to the version id) for stable ordering.
  const draftResults = [...(detail.draft_results ?? [])].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const an = a.document_name || `Draft v${a.label_version_id}`;
    const bn = b.document_name || `Draft v${b.label_version_id}`;
    return an.localeCompare(bn);
  });
  // Page/press-release-shaped results (``ci`` corpus). The backend already
  // orders ``research_job_ci_results`` by ``(rank, id)`` (its own display
  // order — see ``_ci_results_query``'s docstring); re-sort defensively by
  // rank ascending here too so an out-of-order API response still renders
  // correctly, matching the defensive-default pattern of the other lists.
  const ciResults = [...(detail.ci_results ?? [])].sort((a, b) => a.rank - b.rank);
  const isDone = ['complete', 'cancelled', 'error'].includes(job.status);

  // Preserve Document Type on the back link so a user who launched a
  // letter Deep Search → opened results → clicks "Back to Deep Search"
  // returns with the Researcher tab's Document Type still set to
  // fda_letters.  Per the per-surface URL contract, ResearchPanel reads
  // ``?researchSearchMode=`` via useSearchParams to seed and re-sync its
  // local filters.searchMode (NOT ``?searchMode=`` — that's Cogni's
  // namespace, NOT ``?corpus=`` — that param no longer exists).  Drug
  // jobs keep the default URL (no Document Type param) since "labels"
  // is the default and we keep URLs clean.
  const mdrDocTypes = job.doc_types?.length
    ? `&researchSbaDocTypes=${encodeURIComponent(job.doc_types.join(','))}`
    : '';
  const backHref = isApprovalLetterCorpus
    ? '/?mode=researcher&researchSearchMode=approval_letters'
    : isLetterCorpus
    ? '/?mode=researcher&researchSearchMode=fda_letters'
    : isMdrCorpus
      ? `/?mode=researcher&researchSearchMode=multi_review${mdrDocTypes}`
      : isDraftCorpus
        ? '/?mode=researcher&researchSearchMode=label_negotiations'
        : isCiCorpus
          ? '/?mode=researcher&researchSearchMode=ci'
          : '/?mode=researcher';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {stillRunning && (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-[11px] text-slate-500"
               data-testid="investigator-still-running">
            This run is still in progress — CEPI can use it once it finishes.
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            {/* Always rendered, for every corpus. This was previously hidden
                for approval_letters because ApprovalLetterResearchResults
                carried its own copy — but that component only mounts once
                there is at least one result or the job is complete, so a
                pending/running job, or one that ended as error/cancelled with
                zero rows, had NO way back to Deep Search at all. ``backHref``
                already resolves the approval-letter target. */}
            <Link to={backHref} className="text-xs text-indigo-600 hover:text-indigo-800 mb-2 inline-block">
              ← Back to Deep Search
            </Link>
            <h1 className="text-lg font-semibold text-slate-800">
              Investigator Results
              {isMdrCorpus && (
                <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full align-middle">
                  {mdrJobFamily === 'epar' ? 'EMA Assessment (EPAR)' : 'FDA Review (MDR/SBA)'}
                </span>
              )}
              {isDraftCorpus && (
                <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full align-middle">
                  Label Negotiations
                </span>
              )}
              {isCiCorpus && (
                <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full align-middle">
                  Competitive Intel
                </span>
              )}
              {isApprovalLetterCorpus && (
                <span className="ml-2 text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full align-middle">
                  FDA Approval Letters
                </span>
              )}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {copied && <span className="text-xs text-green-600 font-medium">Copied!</span>}
            <button
              onClick={handleShare}
              className="px-3 py-1.5 text-xs text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors"
            >
              Share link
            </button>
          </div>
        </div>

        {/* Query */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 p-6">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Research Question</p>
          <p className="text-sm text-slate-800">{job.query}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
            <StatusBadge status={job.status} />
            <span>{new Date(job.created_at).toLocaleString()}</span>
            {/* Corpus-aware status line (Task 15; ci added Plan-4 Task 5).
               Letter jobs read ``letters_*`` counters (mirrored from
               ``drugs_*`` by the backend when ``corpus == 'fda_letters'``);
               ci jobs reuse ``drugs_*`` directly with a "document(s)" noun
               (no dedicated ``ci_*`` counter pair exists — see the
               ``ResearchJob.corpus`` doc comment above); drug/mdr jobs read
               ``drugs_*`` directly with the "candidates/match(es)" copy. */}
            {isLetterCorpus ? (
              <>
                {!isDone && (job.letters_total ?? 0) > 0 && (
                  <span>
                    Evaluating {job.letters_analyzed ?? 0}/{job.letters_total ?? 0} letters
                  </span>
                )}
                {(job.letters_found ?? 0) > 0 && (
                  <span className="text-indigo-600 font-medium">
                    {job.letters_found} letter match{job.letters_found !== 1 ? 'es' : ''}
                  </span>
                )}
              </>
            ) : isCiCorpus || isApprovalLetterCorpus ? (
              <>
                {!isDone && (
                  <span>
                    {job.drugs_total > 0
                      ? `Evaluating ${job.drugs_analyzed}/${job.drugs_total} documents`
                      : 'Finding documents…'}
                  </span>
                )}
                {job.drugs_found > 0 && (
                  <span className="text-indigo-600 font-medium">
                    {job.drugs_found} document match{job.drugs_found !== 1 ? 'es' : ''}
                  </span>
                )}
              </>
            ) : (
              <>
                {!isDone && (
                  <span>
                    {job.drugs_total > 0
                      ? `Evaluating ${job.drugs_analyzed}/${job.drugs_total} candidates`
                      : 'Finding candidates…'}
                  </span>
                )}
                {job.drugs_found > 0 && (
                  <span className="text-indigo-600 font-medium">
                    {job.drugs_found} match{job.drugs_found !== 1 ? 'es' : ''}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Progress (while running).  Letter jobs prefer the dedicated
           ``letters_*`` counters from the SSE payload; drug jobs use
           the existing drug-named ones.  Backend mirrors them under the
           hood (see ``_job_to_response``), so falling back to the drug
           counters when ``letters_*`` is undefined keeps older payloads
           rendering. */}
        {!isDone && stream.progress && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 p-6">
            {isLetterCorpus ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">
                    Evaluating {stream.progress.letters_analyzed ?? stream.progress.drugs_analyzed}
                    /{stream.progress.letters_total ?? stream.progress.drugs_total} letters
                  </span>
                  <span className="text-sm text-indigo-600 font-medium">
                    {stream.progress.letters_found ?? stream.progress.drugs_found} match
                    {(stream.progress.letters_found ?? stream.progress.drugs_found) !== 1 ? 'es' : ''}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        (stream.progress.letters_total ?? stream.progress.drugs_total) > 0
                          ? Math.round(
                              ((stream.progress.letters_analyzed ?? stream.progress.drugs_analyzed) /
                                (stream.progress.letters_total ?? stream.progress.drugs_total)) *
                                100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </>
            ) : isCiCorpus || isApprovalLetterCorpus ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">
                    {stream.progress.drugs_total > 0
                      ? `Evaluating ${stream.progress.drugs_analyzed}/${stream.progress.drugs_total} documents`
                      : 'Finding documents…'}
                  </span>
                  <span className="text-sm text-indigo-600 font-medium">
                    {stream.progress.drugs_found} document match{stream.progress.drugs_found !== 1 ? 'es' : ''}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${stream.progress.drugs_total > 0 ? Math.round((stream.progress.drugs_analyzed / stream.progress.drugs_total) * 100) : 0}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">
                    {stream.progress.drugs_total > 0
                      ? `Evaluating ${stream.progress.drugs_analyzed}/${stream.progress.drugs_total} candidates`
                      : 'Finding candidates…'}
                  </span>
                  <span className="text-sm text-indigo-600 font-medium">
                    {stream.progress.drugs_found} match{stream.progress.drugs_found !== 1 ? 'es' : ''}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${stream.progress.drugs_total > 0 ? Math.round((stream.progress.drugs_analyzed / stream.progress.drugs_total) * 100) : 0}%` }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Error */}
        {job.status === 'error' && (
          <div className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">
            Research failed: {job.error_message || 'Unknown error'}
          </div>
        )}

        {/* Results — discriminated render by ``job.corpus`` (Task 15).
           Letter jobs reuse ``LetterResultRow`` via a small adapter
           (``letterResearchResultToRowItem``); drug jobs keep the
           existing ``ResultCard`` / ``ClassEvolutionResults`` paths
           untouched so back-compat is bit-for-bit. */}
        {/* Phase 2 stopReasonBanner — copy-only label distinguishing
            max_evaluated (partial-by-construction) from max_matches
            (got enough good answers) and time_budget_exceeded. Renders
            null for the legacy completed path AND for legacy jobs that
            pre-date the metadata stamp. */}
        {isDone && (
          <StopReasonBanner
            stopReason={job.stop_reason}
            totalEvaluated={job.total_evaluated}
            notice={job.notice}
          />
        )}

        {isApprovalLetterCorpus ? (
          (detail.approval_letter_results?.length ?? 0) > 0 ||
          job.status === 'complete' ? (
            <ApprovalLetterResearchResults
              job={{ ...job, approval_letter_results: detail.approval_letter_results ?? [] }}
            />
          ) : null
        ) : isCiCorpus ? (
          ciResults.length > 0 ? (
            <div className="space-y-4">
              {ciResults.map((r, i) => (
                <CiResultRow key={r.source_key} result={r} index={i} />
              ))}
            </div>
          ) : null
        ) : isDraftCorpus ? (
          draftResults.length > 0 ? (
            <div className="space-y-4">
              {draftResults.map((r, i) => (
                <DraftResultRow
                  key={`draft-${r.label_version_id}-${i}`}
                  result={r}
                  index={i}
                />
              ))}
            </div>
          ) : null
        ) : isLetterCorpus ? (
          letterResults.length > 0 ? (
            <div className="space-y-4">
              {letterResults.map((r, i) => (
                <LetterResultRow
                  key={`letter-${r.letter_id}-${i}`}
                  letter={letterResearchResultToRowItem(r)}
                />
              ))}
            </div>
          ) : null
        ) : results.length > 0 && job.strategy_type === 'class_evolution' ? (
          <ClassEvolutionResults job={job} results={results} />
        ) : results.length > 0 ? (
          <div className="space-y-4">
            {results.map((r, i) => (
              <ResultCard key={`${r.drug_id}-${i}`} result={r} index={i} corpus={job.corpus} />
            ))}
          </div>
        ) : null}

        {/* No results — corpus-aware empty message so the user sees the
           right noun for the corpus they ran in. */}
        {isDone &&
          job.status !== 'error' &&
          ((isCiCorpus && ciResults.length === 0) ||
            (isDraftCorpus && draftResults.length === 0) ||
            (isLetterCorpus && letterResults.length === 0) ||
            (!isLetterCorpus && !isDraftCorpus && !isCiCorpus && !isApprovalLetterCorpus && results.length === 0)) && (
            <div className="text-sm text-slate-500 bg-slate-50 px-4 py-6 rounded-lg text-center">
              {isLetterCorpus
                ? 'No matching FDA Enforcement Letters found for this research query.'
                : isDraftCorpus
                  ? 'No matching draft labels found for this research query.'
                  : isCiCorpus
                    ? 'No matching competitive-intel documents found for this research query.'
                    : 'No matching drugs found for this research query.'}
            </div>
          )}

      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    running: 'bg-blue-100 text-blue-700',
    analyzing: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  const label = ['running', 'analyzing'].includes(status) ? 'running' : status;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-500'}`}>
      {label}
    </span>
  );
}

function ResultCard({ result: r, index, corpus }: { result: ResearchResult; index: number; corpus?: string | undefined }) {
  const [expanded, setExpanded] = useState(false);

  const toolResults = r.evidence?.tool_results || {};
  const sectionsReferenced = new Set<string>();
  Object.values(toolResults).forEach((val: any) => {
    if (val?.args?.section_number) sectionsReferenced.add(val.args.section_number);
    if (val?.args?.sections) val.args.sections.forEach((s: string) => sectionsReferenced.add(s));
  });
  const sectionLinks = Array.from(sectionsReferenced).sort();
  const primaryHref = resultPrimaryHref(r, corpus);
  const isSbaCorpus = corpus === 'multi_review';
  const reviewSources = isSbaCorpus ? multiReviewSources(r) : [];

  const scorePercent = Math.round(r.score * 100);
  const confidenceLabel = `${scorePercent}%`;
  const confidenceBadge = scorePercent >= 90
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : scorePercent >= 70
      ? 'bg-sky-50 text-sky-700 border-sky-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const borderColor = scorePercent >= 90
    ? 'border-l-emerald-500'
    : scorePercent >= 70
      ? 'border-l-sky-400'
      : 'border-l-amber-400';

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden border-l-4 ${borderColor}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-indigo-600">#{index + 1}</span>
          <div className="flex items-center gap-2">
            <a
              href={primaryHref}
              onClick={(e) => e.stopPropagation()}
              className="text-base font-semibold text-slate-800 hover:text-indigo-600 transition-colors"
            >
              {r.drug_name}
            </a>
            {r.application_number && (
              <span className="text-sm text-slate-400">{r.application_number}</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${confidenceBadge}`}>
              {confidenceLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSbaCorpus ? (
            // HA corpus: one chip per logical source document/version.
            reviewSources.slice(0, 3).map((source) => {
              const chunk = source.representative;
              const chipHref = regulatoryCitationHref({
                document_type: (chunk as any).document_type ?? 'sba',
                drug_id: r.drug_id,
                document_id: chunk.document_id,
                chunk_id: chunk.chunk_id,
              } as any) ?? primaryHref;
              return (
                <a
                  key={source.key}
                  href={chipHref}
                  onClick={(e) => e.stopPropagation()}
                  className="px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                  title={
                    chunk.source_document_name
                      ? `${reviewSourceLabel(source)} · ${chunk.source_document_name}`
                      : reviewSourceLabel(source)
                  }
                >
                  {reviewSourceLabel(source, true)}
                </a>
              );
            })
          ) : (
            sectionLinks.slice(0, 3).map((sec) => (
              <a
                key={sec}
                href={`/drug/${r.drug_id}?section=${sec}`}
                onClick={(e) => e.stopPropagation()}
                className="px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                title={sectionLabels[sec] || `Section ${sec}`}
              >
                §{sec}
              </a>
            ))
          )}
          {isSbaCorpus
            ? reviewSources.length > 3 && (
                <span className="text-xs text-slate-400">+{reviewSources.length - 3}</span>
              )
            : sectionLinks.length > 3 && (
                <span className="text-xs text-slate-400">+{sectionLinks.length - 3}</span>
              )
          }
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ml-1 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-100">
          <div className="px-6 py-4">
            <div
              className="text-sm text-slate-600 leading-relaxed prose prose-sm prose-slate max-w-none
                         [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-700 [&_h3]:mt-3 [&_h3]:mb-1
                         [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-slate-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.analysis || '', { USE_PROFILES: { html: true } }) }}
            />
          </div>

          <div className="px-6 pb-4 space-y-3">
            {isSbaCorpus ? (
              // HA corpus: one citation per logical source document/version.
              reviewSources.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                    {multiReviewFamily(r) === 'epar' ? 'EMA Assessment (EPAR)' : 'FDA Review (MDR/SBA)'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {reviewSources.map((source) => {
                      const chunk = source.representative;
                      const chipHref = regulatoryCitationHref({
                        document_type: (chunk as any).document_type ?? 'sba',
                        drug_id: r.drug_id,
                        document_id: chunk.document_id,
                        chunk_id: chunk.chunk_id,
                      } as any) ?? primaryHref;
                      return (
                        <a
                          key={source.key}
                          href={chipHref}
                          className="inline-flex flex-col items-start px-2.5 py-1 rounded-md text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors border border-indigo-100"
                        >
                          <span>{reviewSourceLabel(source)}</span>
                          {chunk.source_document_name && (
                            <span className="text-[10px] text-indigo-500 max-w-64 truncate">
                              {chunk.source_document_name}
                            </span>
                          )}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )
            ) : (
              <>
                {sectionLinks.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                      Sections referenced
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {sectionLinks.map((sec) => (
                        <a
                          key={sec}
                          href={`/drug/${r.drug_id}?section=${sec}`}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors border border-indigo-100"
                        >
                          §{sec} {sectionLabels[sec] && `– ${sectionLabels[sec]}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(toolResults).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Sources</p>
                    <div className="space-y-1">
                      {Object.entries(toolResults).map(([key, val]) => {
                        const toolName = key.replace(/_\d+$/, '');
                        return (
                          <div key={key} className="text-xs text-slate-500 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
                            <span>{toolLabels[toolName] || toolName}</span>
                            {(val as any)?.args?.section_number && (
                              <a href={resultSectionHref(r, (val as any).args.section_number)} className="text-indigo-600 hover:text-indigo-800">
                                §{(val as any).args.section_number}
                              </a>
                            )}
                            {(val as any)?.args?.sections && (val as any).args.sections.map((s: string) => (
                              <a key={s} href={resultSectionHref(r, s)} className="text-indigo-600 hover:text-indigo-800 mr-1">§{s}</a>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="pt-1">
              <a
                href={primaryHref}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                {isSbaCorpus
                  ? multiReviewFamily(r) === 'epar' ? 'View EMA assessment →' : 'View FDA review →'
                  : 'View full drug label →'}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Competitive-Intel (ci) Result Row
// ---------------------------------------------------------------------------

/**
 * Render one competitive-intel research result (a company product page or
 * press release). Unlike every other corpus row on this page, CI has no
 * in-app viewer to route to — the ONLY click target is the competitor's own
 * page, gated through ``safeCompetitiveIntelUrl`` (HTTPS-only) so a bad/
 * malformed stored ``page_url`` can never become a link.
 */
function CiResultRow({ result: r, index }: { result: CIResearchResult; index: number }) {
  const safeUrl = safeCompetitiveIntelUrl(r.page_url);
  const title = r.title || r.company_name;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden px-6 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg font-bold text-indigo-600 shrink-0">#{index + 1}</span>
          <div className="min-w-0">
            {safeUrl ? (
              <a
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-semibold text-slate-800 hover:text-indigo-600 transition-colors"
              >
                {title}
              </a>
            ) : (
              <span className="text-base font-semibold text-slate-800">{title}</span>
            )}
            <div className="text-xs text-slate-400">{r.company_name}</div>
          </div>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">
          {r.doc_type === 'press_release' ? 'Press Release' : 'Product Page'}
        </span>
      </div>
      {r.summary && (
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{r.summary}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draft (label_negotiations) Result Row
// ---------------------------------------------------------------------------

/**
 * Render one draft-document research result.  Mirrors the ``ResultCard``
 * layout (expandable card, score badge, answer body) but is keyed on the
 * draft document (``label_version_id``) and renders its ``snippets`` badged
 * by ``kind`` (content vs comment).  Click-through deep-links into the draft
 * viewer via ``draftResultHref`` (→ ``resolveDraftTarget``): the row title /
 * content snippets open the draft root; comment snippets open at the comment.
 */
function DraftResultRow({ result: r, index }: { result: DraftResearchResultResponse; index: number }) {
  const [expanded, setExpanded] = useState(false);

  const title =
    r.document_name ||
    (r.drug_id != null ? `Drug ${r.drug_id}` : `Draft v${r.label_version_id}`);
  const rowHref = draftResultHref(r);
  const snippets = r.snippets ?? [];

  const scorePercent = Math.round(r.score * 100);
  const confidenceLabel = `${scorePercent}%`;
  const confidenceBadge = scorePercent >= 90
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : scorePercent >= 70
      ? 'bg-sky-50 text-sky-700 border-sky-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  const borderColor = scorePercent >= 90
    ? 'border-l-emerald-500'
    : scorePercent >= 70
      ? 'border-l-sky-400'
      : 'border-l-amber-400';

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden border-l-4 ${borderColor}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-indigo-600">#{index + 1}</span>
          <div className="flex items-center gap-2">
            {rowHref ? (
              <a
                href={rowHref}
                onClick={(e) => e.stopPropagation()}
                className="text-base font-semibold text-slate-800 hover:text-indigo-600 transition-colors"
              >
                {title}
              </a>
            ) : (
              <span className="text-base font-semibold text-slate-800">{title}</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${confidenceBadge}`}>
              {confidenceLabel}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {snippets.length} hit{snippets.length !== 1 ? 's' : ''}
          </span>
          <svg
            className={`w-4 h-4 text-slate-400 transition-transform ml-1 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-100">
          {r.answer && (
            <div className="px-6 py-4">
              <div
                className="text-sm text-slate-600 leading-relaxed prose prose-sm prose-slate max-w-none
                           [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-700 [&_h3]:mt-3 [&_h3]:mb-1
                           [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-slate-600"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.answer, { USE_PROFILES: { html: true } }) }}
              />
            </div>
          )}

          {snippets.length > 0 && (
            <div className="px-6 pb-4 space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Matched snippets
              </p>
              {snippets.map((hit, hi) => {
                const hitHref = draftResultHref(r, hit);
                const isComment = hit.kind === 'comment';
                const body = (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                        isComment ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {hit.kind}
                      </span>
                      {hit.section_ref && (
                        <span className="text-xs text-slate-400">§{hit.section_ref}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{hit.snippet}</p>
                  </>
                );
                return hitHref ? (
                  <a
                    key={`snip-${hi}`}
                    href={hitHref}
                    className="block bg-slate-50 border border-slate-200/60 rounded-lg p-3 hover:border-indigo-200 hover:bg-white transition-all"
                  >
                    {body}
                  </a>
                ) : (
                  <div
                    key={`snip-${hi}`}
                    className="block bg-slate-50 border border-slate-200/60 rounded-lg p-3"
                  >
                    {body}
                  </div>
                );
              })}
            </div>
          )}

          {rowHref && (
            <div className="px-6 pb-4 pt-1">
              <a
                href={rowHref}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                View draft label →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Class Evolution Results
// ---------------------------------------------------------------------------

const changeTypeBadge: Record<string, { label: string; color: string }> = {
  safety_update: { label: 'Safety', color: 'bg-amber-100 text-amber-800' },
  new_indication: { label: 'Indication', color: 'bg-green-100 text-green-800' },
  new_trial_data: { label: 'Trial Data', color: 'bg-blue-100 text-blue-800' },
  boxed_warning: { label: 'Boxed Warning', color: 'bg-red-100 text-red-800' },
  dosing_change: { label: 'Dosing', color: 'bg-purple-100 text-purple-800' },
  other: { label: 'Other', color: 'bg-slate-100 text-slate-700' },
};

function ClassEvolutionResults({ job, results }: { job: ResearchJob; results: ResearchResult[] }) {
  return (
    <div className="space-y-6">
      {/* Cohort header */}
      <div className="bg-indigo-50 rounded-lg px-4 py-3">
        <h3 className="text-sm font-semibold text-indigo-900">
          Evolution Analysis: {results.length} drugs
        </h3>
      </div>

      {/* Synthesis narrative */}
      {job.summary && (
        <div
          className="prose prose-sm max-w-none bg-white rounded-lg p-4 border border-slate-200"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.summary, { USE_PROFILES: { html: true } }) }}
        />
      )}

      {/* Patterns */}
      {job.synthesis?.patterns && job.synthesis.patterns.length > 0 && (
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <h4 className="text-sm font-semibold text-slate-800 mb-2">Key Patterns</h4>
          <div className="space-y-2">
            {job.synthesis.patterns.map((p, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium text-slate-700">{p.name}:</span>{' '}
                <span className="text-slate-600">{p.description}</span>
                {p.drugs.length > 0 && (
                  <span className="text-xs text-slate-400 ml-1">({p.drugs.join(', ')})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-drug timeline cards */}
      <div className="space-y-4">
        {results.map((r, i) => (
          <EvolutionCard key={`${r.drug_id}-${i}`} result={r} />
        ))}
      </div>
    </div>
  );
}

function EvolutionCard({ result: r }: { result: ResearchResult }) {
  const [expanded, setExpanded] = useState(false);

  // Parse timeline from analysis JSON
  let timeline: Array<{
    version_pair: string;
    date: string;
    sections_changed: string[];
    change_type: string;
    summary: string;
  }> = [];
  let highlights: { indications?: string; key_safety?: string } = {};

  try {
    if (r.analysis) {
      const parsed = JSON.parse(r.analysis);
      timeline = parsed.timeline || [];
      highlights = parsed.current_highlights || {};
    }
  } catch {
    // analysis may be plain text for older results
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-900">{r.drug_name}</span>
          {r.application_number && (
            <span className="text-xs text-slate-500">{r.application_number}</span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            timeline.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {timeline.length > 0 ? `${timeline.length} changes` : 'No version history'}
          </span>
        </div>
        <span className="text-slate-400">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {/* Current label highlights */}
          {(highlights.indications || highlights.key_safety) && (
            <div className="mt-3 space-y-1 text-sm text-slate-600">
              {highlights.indications && (
                <p><strong>Indications:</strong> {highlights.indications}</p>
              )}
              {highlights.key_safety && (
                <p><strong>Key safety:</strong> {highlights.key_safety}</p>
              )}
            </div>
          )}

          {/* Timeline entries */}
          {timeline.length > 0 ? (
            <div className="mt-3 space-y-2">
              {timeline.map((entry, j) => {
                const badge = changeTypeBadge[entry.change_type] || changeTypeBadge['other'];
                const header = formatTimelineHeader(entry.version_pair, entry.date);
                return (
                  <div key={j} className="flex gap-3 text-sm">
                    <div className="flex-shrink-0 w-56 text-slate-500 pt-0.5 font-medium">
                      {header}
                    </div>
                    <div className="flex-1">
                      <span className={`inline-block text-xs px-1.5 py-0.5 rounded mr-2 ${badge?.color || 'bg-slate-100 text-slate-700'}`}>
                        {badge?.label || 'Other'}
                      </span>
                      {entry.summary}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500 italic">
              No version history available for this drug.
            </p>
          )}

          <div className="pt-2 mt-2 border-t border-slate-100">
            <a
              href={`/drug/${r.drug_id}`}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              View full drug label →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
